/**
 * @fileoverview Aggregated, alias-aware artwork catalog.
 * @module ui-artwork/catalog
 *
 * Responsibilities: Publish complete stable artwork metadata and legacy aliases.
 * Dependencies: Catalog partitions.
 * Architecture: Static data registry consumed by queries and renderers.
 * Notes: IDs are unique; aliases never replace the canonical persisted ID.
 */
import type { ArtworkCatalogEntry } from '../types';
import { BADGE_ARTWORK } from './badges';
import { GAP_3D_ARTWORK } from './gapAssets';
import { GLYPH_ARTWORK, resolveLucideName } from './glyphs';
import { OPERATIONAL_ARTWORK } from './operational';
import { THREE_D_ARTWORK } from './threeD';

export const ARTWORK_CATALOG: ArtworkCatalogEntry[] = [
  ...THREE_D_ARTWORK,
  ...GAP_3D_ARTWORK,
  ...OPERATIONAL_ARTWORK,
  ...BADGE_ARTWORK,
  ...GLYPH_ARTWORK,
];

const byId = new Map(ARTWORK_CATALOG.map((entry) => [entry.id, entry]));
const byAlias = new Map<string, ArtworkCatalogEntry>();
for (const entry of ARTWORK_CATALOG) {
  for (const alias of entry.legacyAliases ?? []) byAlias.set(alias.toLowerCase(), entry);
}
for (const entry of OPERATIONAL_ARTWORK) {
  for (const alias of entry.legacyAliases ?? []) byAlias.set(alias.toLowerCase(), entry);
}

const FIRE_SERVICE_ALIASES: Record<string, string> = {
  'fs-fire-extinguisher': 'art:3d/equip-extinguisher',
  'fs-ladder-truck': 'art:3d/ladder-truck-tiller',
  'fs-fire-engine': 'art:3d/apparatus-engine',
  'fs-ambulance': 'art:3d/apparatus-ambulance',
  'fs-smoke-detector': 'art:ops/sys-smoke-detector',
  'fs-sprinkler-head': 'art:ops/sys-sprinkler-riser',
  'fs-hazmat-placard': 'art:ops/hazmat-placard',
  'fs-nfpa-diamond': 'art:ops/nfpa-704-diamond',
  'fs-aed': 'art:ops/equip-aed',
  'fs-hose-nozzle': 'art:ops/water-fdc',
  'fs-maltese-cross': 'glyph:shield',
  'fs-fire-alarm-pull': 'art:ops/sys-facp',
  'fs-exit-sign': 'glyph:chevron-right',
  'fs-inspection-badge': 'art:badge/progress-active',
  'fs-deficiency-flag': 'art:badge/status-violation',
  'fs-high-rise': 'art:3d/commercial-office',
};
const LEGACY_GAME_ICON_ALIASES: Record<string, string> = {
  'gi-modern-city': 'art:3d/apartment-complex',
  gimoderncity: 'art:3d/apartment-complex',
  'gi-fire-hydrant': 'art:ops/water-hydrant',
  gifirehydrant: 'art:ops/water-hydrant',
  'gi-fire-engine': 'art:3d/apparatus-engine',
  gifireengine: 'art:3d/apparatus-engine',
};
for (const [alias, id] of Object.entries({ ...FIRE_SERVICE_ALIASES, ...LEGACY_GAME_ICON_ALIASES })) {
  const entry = byId.get(id);
  if (entry) byAlias.set(alias, entry);
}

/**
 * @reusable getArtworkEntry
 * Usage: Resolve a canonical catalog ID or legacy artwork/icon name.
 * Rules: Unknown names return undefined; valid Lucide icon names resolve to
 *   a dynamic glyph entry so persisted legacy icon names remain renderable.
 */
export function getArtworkEntry(idOrAlias: string | undefined | null): ArtworkCatalogEntry | undefined {
  if (!idOrAlias) return undefined;
  const id = idOrAlias.trim();
  const direct = byId.get(id);
  if (direct) return direct;
  const alias = byAlias.get(id.toLowerCase());
  if (alias) return alias;
  if (id.toLowerCase() === 'art:3d/derelict-vacant') return byId.get('art:3d/derelict-vacant');
  const glyph = resolveLucideName(id);
  if (glyph) return byId.get(`glyph:${glyph}`) ?? {
    id: `glyph:${glyph}`,
    label: glyph,
    category: 'ui',
    style: 'glyph',
    keywords: [glyph.replace(/-/g, ' ')],
    colorPolicy: 'customizable',
    defaultColor: '#475569',
    source: 'lucide-glyph',
    attribution: 'Lucide, ISC License',
  };
  return undefined;
}
